import clsx from 'clsx';
import React, { useCallback, useEffect } from 'react';
import { useParams } from 'react-router';

import { ICONS } from '@/src/components/common/Elements/Icon/Icon';
import { isResponseError } from '@/src/components/project/Inbox2/api/config';
import ActionButton from '@/src/components/project/Inbox2/Elements/ActionButton/ActionButton';
import { showToast } from '@/src/globalFunction';

import useSyncInboxEntry from '../../../../../useSyncInboxEntry';
import { useValidateActionResponse } from '../../../../../useValidateActionResponse';
import { useMarkMiscellaneous } from '../../../../api/markMiscellaneousEmail';
import { useInbox } from '@/src/components/project/Inbox2/store/inbox.store';

const getLabel = (isMisc) => {
  return isMisc ? 'Unmark as Miscellaneous' : 'Mark as Miscellaneous';
};

const getSuccessMessage = (isMisc) => {
  return isMisc ? 'Unmarked as Miscellaneous' : 'Marked as Miscellaneous';
};

type MarkMiscellaneousProps = {
  emailId: number;
  inboxEntryId: number;
  isMisc: boolean;
  onSuccess?: (response: any) => void;
};
const MarkMiscellaneous = ({
  inboxEntryId,
  emailId,
  isMisc,
  onSuccess = null,
}: MarkMiscellaneousProps) => {
  const label = getLabel(isMisc);
  const { projectId } = useParams<{ projectId: string }>();
  const markMiscellaneousMutation = useMarkMiscellaneous({ projectId, emailId, inboxEntryId });
  const { confirmRefreshModal } = useValidateActionResponse({
    responseError: isResponseError(markMiscellaneousMutation.data)
      ? markMiscellaneousMutation.data
      : null,
  });

  const syncInboxEntry = useSyncInboxEntry();

  const handleMarkMiscellaneous = useCallback(async () => {
    const response = await markMiscellaneousMutation.mutateAsync({
      projectId,
      emailId,
      inboxEntryId,
      misc: !isMisc,
    });

    if (isResponseError(response)) {
      return;
    }

    if (response) {
      if (onSuccess) {
        onSuccess(response);
      }

      syncInboxEntry(response.data['inbox-entry']);
      showToast('success', getSuccessMessage(isMisc));
    }
  }, [
    emailId,
    projectId,
    inboxEntryId,
    isMisc,
    markMiscellaneousMutation,
    onSuccess,
    syncInboxEntry,
  ]);

  const { setIsMarkAsMiscellaneousClicked } = useInbox();

  useEffect(() => {
    setIsMarkAsMiscellaneousClicked(markMiscellaneousMutation.isLoading);
  }, [markMiscellaneousMutation.isLoading, setIsMarkAsMiscellaneousClicked]);

  return (
    <React.Fragment>
      <ActionButton
        id={`markMiscellaneous_${inboxEntryId}`}
        className={clsx(isMisc && 'active')}
        onClick={handleMarkMiscellaneous}
        icon={ICONS.miscellaneous_icon({
          mainClassName: clsx(
            'group-hover:tw-fill-cfo-blue-700 group-hover:tw-stroke-cfo-blue-700',
            isMisc && 'tw-fill-cfo-blue-700 tw-stroke-cfo-blue-700'
          ),
          fillClassName: clsx('group-hover:tw-fill-white', isMisc && 'tw-fill-white'),
        })}
        tooltipSettings={{
          content: label,
          place: 'left',
        }}
      />
      {confirmRefreshModal}
    </React.Fragment>
  );
};

export default MarkMiscellaneous;
